import { useDebounceFn } from '@vueuse/core'
import type { ParsedContent } from '@nuxt/content'

export default async function useUseCasesSearch() {
  const i18n = useI18n()

  const itemsPerPage = useState('use-cases-search-items-per-page', () => 5)
  const loading = useState('use-cases-search-loading', () => false)
  const page = useState('use-cases-search-page', () => 1)
  const search = useState('use-cases-search-search', () => '')
  const total = useState('use-cases-search-total', () => 0)
  const enabledTags = useRouteQuery<string>('tags', () => '')
  const availableTags = computed(() => {
    const tags = [
      'marketing',
      'sales',
      'product',
      'user-experience',
    ]

    return tags.map(
      tag => ({
        label: changeCase.capitalCase(i18n.t(`useCases.tags.${tag}`)),
        tag,
      }),
    )
  })

  const results = useState<(ParsedContent & { matches?: {
    body?: string[]
    description?: string
    title?: string
  } })[]>('use-cases-search-results', () => [])

  // Methods
  const fetchResults = async () => {
    try {
      loading.value = true

      const content = await $fetch<{ results: ParsedContent[], total: number }>(
        '/api/server/content/search',
        {
          params: {
            locale: i18n.locale.value,
            page: page.value,
            search: search.value,
            tags: enabledTags.value,
          },
        },
      )

      total.value = content.total

      // Loading search results
      if (search.value || page.value === 1) {
        results.value = content.results

        if (search.value) {
          page.value = 1
        }
      }
      else {
        const slugs = results.value.map(item => item.slug)

        results.value.push(...content.results.filter(result => !slugs.includes(result.slug)))
      }
    }
    catch (error) {
      console.error('Error fetching results:', error)
      total.value = 0
      results.value = []
    }
    finally {
      loading.value = false
    }
  }

  const debouncedFetchResults = useDebounceFn(fetchResults, 250)

  const onLoadMore = () => {
    if (total.value && page.value * itemsPerPage.value >= total.value) {
      return
    }

    page.value++
  }

  const onToggleTag = (tag: string) => {
    const tags = enabledTags.value.split(',').filter(Boolean)

    if (tags.includes(tag)) {
      enabledTags.value = tags.filter(t => t !== tag).join(',')
    }
    else {
      enabledTags.value = [...tags, tag].join(',')
    }
  }

  watch([search, enabledTags], () => {
    return debouncedFetchResults()
  })

  watch(page, (newPage, oldPage) => {
    if (newPage > oldPage) {
      fetchResults()
    }
  })

  await fetchResults()

  return {
    activeTags: computed(() => {
      return enabledTags.value.split(',').filter(Boolean)
    }),
    availableTags,
    loading,
    onLoadMore,
    onToggleTag,
    page,
    results,
    search,
    total,
  }
}
